import { useEffect, useState } from 'react';

/*
 * Слежение за версией приложения.
 *
 * Вкладку держат открытой днями, а выкладки идут своим чередом. Старый
 * JavaScript продолжает разговаривать с новым сервером: поля переименованы,
 * связи изменены — и человек видит пустые списки, которые невозможно
 * воспроизвести. Одна такая вкладка стоила нам вечера разбирательств.
 *
 * Версию определяем по имени собранного файла в index.html: оно меняется
 * при каждой сборке, а сам index.html отдаётся без кэша. Ничего добавлять
 * в сборку не нужно.
 */

const BUNDLE_RX = /assets\/index-[A-Za-z0-9_-]+\.js/;

/** Файл, с которого работает эта вкладка. */
export function currentBundle(): string | null {
  for (const el of document.querySelectorAll('script[src]')) {
    const m = (el as HTMLScriptElement).src.match(BUNDLE_RX);
    if (m) return m[0];
  }
  return null;
}

/** Файл, который сервер отдаёт сейчас. */
async function publishedBundle(): Promise<string | null> {
  try {
    const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return null;
    return (await res.text()).match(BUNDLE_RX)?.[0] ?? null;
  } catch {
    // нет сети — не наше дело, проверим в следующий раз
    return null;
  }
}

/** Как часто сверяться с сервером, пока вкладка открыта. */
const CHECK_EVERY_MS = 10 * 60_000;

/**
 * Возвращает true, когда на сервере лежит уже другая сборка. Проверяет
 * при возвращении на вкладку и раз в десять минут — обновление не должно
 * зависеть от того, вспомнит ли человек нажать F5.
 */
export function useAppOutdated(): boolean {
  const [outdated, setOutdated] = useState(false);

  useEffect(() => {
    const mine = currentBundle();
    // в режиме разработки собранного файла нет — следить не за чем
    if (!mine) return;

    let stopped = false;
    const check = async () => {
      if (stopped || document.hidden) return;
      const latest = await publishedBundle();
      if (latest && latest !== mine) setOutdated(true);
    };

    const onVisible = () => {
      if (!document.hidden) void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, CHECK_EVERY_MS);
    void check();

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, []);

  return outdated;
}
